import type {ComponentFeatures, Namespace} from '@wix/document-services-types'
import type {ExtendedRefInfo, UnmarkedRefInfo} from '../types'
import _ from 'lodash'

export const STRUCTURE_TYPE = 'components'
const DESKTOP = 'DESKTOP'

export const DATA_TYPES = {
    variants: 'variants',
    data: 'data',
    reactions: 'reactions',
    style: 'style',
    variables: 'variables',
    innerElements: 'innerElements',
    triggers: 'triggers',
    behaviors: 'behaviors',
    connections: 'connections',
    design: 'design',
    naming: 'naming',
    presets: 'presets',
    slots: 'slots',
    transformations: 'transformations'
}

export const unmarkedReferenceBaseData: Partial<ExtendedRefInfo> = {
    isRefOwner: false,
    noHash: true
}

const RICH_TEXT_REF_TYPES = [
    'PageLink',
    'ExternalLink',
    'EmailLink',
    'AnchorLink',
    'DocumentLink',
    'PhoneLink',
    'DynamicPageLink',
    'TextLink',
    'LoginToWixLink',
    'WhatsAppLink',
    'AddressLink',
    'TpaPageLink'
]

const polymorphicVariantRelationCandidateMaps = [STRUCTURE_TYPE, DATA_TYPES.innerElements, DATA_TYPES.variants]

const polymorphicVariantRelationResolution = {
    candidateMaps: polymorphicVariantRelationCandidateMaps,
    mustResolveExactlyOne: true
}

const polymorphicVariantRelationFromRef: UnmarkedRefInfo = {
    referencedMap: STRUCTURE_TYPE,
    path: ['from'],
    ...polymorphicVariantRelationResolution
}

const polymorphicVariantRelationFromSpecialRef: Partial<ExtendedRefInfo> = {
    noHash: false,
    ...polymorphicVariantRelationResolution
}

// TODO: Change after DM-8982 is done
export const unmarkedReferences: Record<string, Record<string, UnmarkedRefInfo[]>> = {
    variants: {
        Hover: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        Mobile: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        State: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        Selected: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        Trigger: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        Preset: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        RepeaterPattern: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        RepeaterPatternList: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        VariantsList: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        BreakpointsData: [{referencedMap: STRUCTURE_TYPE, path: ['componentId']}],
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    reactions: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    style: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    innerElements: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    triggers: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    design: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    presets: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    slots: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    transformations: {
        VariantRelation: [polymorphicVariantRelationFromRef]
    },
    data: {
        ImageX: [{referencedMap: DATA_TYPES.variants, path: ['scopedData', 'breakpoint'], noHash: false}],
        StyledText: [{referencedMap: DATA_TYPES.data, path: ['text'], refTypes: RICH_TEXT_REF_TYPES, noHash: false}],
        MediaRichText: [{referencedMap: DATA_TYPES.data, path: ['text'], refTypes: RICH_TEXT_REF_TYPES, noHash: false}]
    }
}

export const namespaceToReferencedMap: Record<Namespace, string> = {
    [DESKTOP]: STRUCTURE_TYPE
}
export const referencedMapToNamespace: Record<string, Namespace> = _.invert(namespaceToReferencedMap)

export const specialReferences: Record<string, Record<string, Record<string, Partial<ExtendedRefInfo>>>> = {
    connections: {
        ConnectionList: {
            'items.controllerId': {
                noHash: true
            }
        }
    },
    data: {
        InternalRef: {
            rootCompId: {noHash: true, referencedMap: STRUCTURE_TYPE}
        },
        MultiStateBoxData: {
            defaultStateId: {noHash: true, constantValues: new Set(['FIRST_STATE'])},
            selectedStateId: {noHash: true}
        },
        AnchorLink: {
            anchorDataId: {constantValues: new Set(['SCROLL_TO_TOP', 'SCROLL_TO_BOTTOM'])}
        }
    },
    layout: {
        SingleLayoutData: {
            'variableConnections.variableId': {noHash: true}
        }
    },
    slots: {
        PlaceholderSlot: {
            'slots.slot': {noHash: true}
        }
    },
    triggers: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    design: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    presets: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    transformations: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    variables: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    innerElements: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    style: {
        '*': {
            'variableConnections.variableId': {noHash: true}
        },
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    },
    reactions: {
        VariantRelation: {from: polymorphicVariantRelationFromSpecialRef}
    }
}

export const specialComponentFeatures: ComponentFeatures = {
    variants: {
        query: 'variantsQuery',
        namespace: 'variants',
        alias: 'variants'
    },
    breakpointVariants: {
        query: 'breakpointVariantsQuery',
        namespace: 'variants',
        alias: 'breakpointVariants'
    }
}
